(() => {
  const formatMillimeters = (value) => {
    const text = String(value || '').trim();
    if (!text) return '';
    return /mm$/i.test(text) ? text : `${text} mm`;
  };

  const waitForImage = (image) => {
    if (image.complete && image.naturalWidth) return Promise.resolve();

    return new Promise((resolve, reject) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', reject, { once: true });
    });
  };

  const findAlphaBounds = async (image) => {
    await waitForImage(image);

    const maxDimension = 420;
    const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });

    canvas.width = width;
    canvas.height = height;
    context.drawImage(image, 0, 0, width, height);

    let pixels;
    try {
      pixels = context.getImageData(0, 0, width, height).data;
    } catch (error) {
      return null;
    }

    const size = width * height;
    const mask = new Uint8Array(size);
    const visited = new Uint8Array(size);
    const stack = new Int32Array(size);
    const components = [];

    for (let index = 0; index < size; index += 1) {
      if (pixels[index * 4 + 3] > 18) mask[index] = 1;
    }

    for (let start = 0; start < size; start += 1) {
      if (!mask[start] || visited[start]) continue;

      let stackSize = 1;
      let area = 0;
      let left = width;
      let top = height;
      let right = -1;
      let bottom = -1;
      stack[0] = start;
      visited[start] = 1;

      while (stackSize > 0) {
        const index = stack[(stackSize -= 1)];
        const x = index % width;
        const y = Math.floor(index / width);
        area += 1;
        left = Math.min(left, x);
        top = Math.min(top, y);
        right = Math.max(right, x);
        bottom = Math.max(bottom, y);

        if (x > 0) {
          const neighbor = index - 1;
          if (mask[neighbor] && !visited[neighbor]) {
            visited[neighbor] = 1;
            stack[stackSize] = neighbor;
            stackSize += 1;
          }
        }
        if (x < width - 1) {
          const neighbor = index + 1;
          if (mask[neighbor] && !visited[neighbor]) {
            visited[neighbor] = 1;
            stack[stackSize] = neighbor;
            stackSize += 1;
          }
        }
        if (y > 0) {
          const neighbor = index - width;
          if (mask[neighbor] && !visited[neighbor]) {
            visited[neighbor] = 1;
            stack[stackSize] = neighbor;
            stackSize += 1;
          }
        }
        if (y < height - 1) {
          const neighbor = index + width;
          if (mask[neighbor] && !visited[neighbor]) {
            visited[neighbor] = 1;
            stack[stackSize] = neighbor;
            stackSize += 1;
          }
        }
      }

      components.push({ area, left, top, right, bottom });
    }

    if (!components.length) return null;

    components.sort((first, second) => second.area - first.area);
    const primaryStone = components[0];
    const significantComponents = components.filter((component) => component.area >= primaryStone.area * 0.3);
    const stoneComponents = significantComponents.length > 1 ? significantComponents : [primaryStone];
    const stageStone = stoneComponents.reduce(
      (bounds, component) => ({
        left: Math.min(bounds.left, component.left),
        top: Math.min(bounds.top, component.top),
        right: Math.max(bounds.right, component.right),
        bottom: Math.max(bounds.bottom, component.bottom),
      }),
      { ...primaryStone }
    );
    const padBounds = (bounds) => {
      const padding = Math.max(1, Math.round(Math.max(bounds.right - bounds.left, bounds.bottom - bounds.top) * 0.015));
      return {
        left: Math.max(0, bounds.left - padding),
        top: Math.max(0, bounds.top - padding),
        right: Math.min(width - 1, bounds.right + padding),
        bottom: Math.min(height - 1, bounds.bottom + padding),
      };
    };
    const { left, top, right, bottom } = padBounds(stageStone);
    const dimensionBounds = padBounds(primaryStone);

    const cropWidth = right - left + 1;
    const cropHeight = bottom - top + 1;

    return {
      left,
      top,
      width: cropWidth,
      height: cropHeight,
      sourceWidth: width,
      sourceHeight: height,
      isMultiStone: stoneComponents.length > 1,
      stoneCount: stoneComponents.length,
      dimensionLeft: dimensionBounds.left,
      dimensionTop: dimensionBounds.top,
      dimensionWidth: dimensionBounds.right - dimensionBounds.left + 1,
      dimensionHeight: dimensionBounds.bottom - dimensionBounds.top + 1,
      shadowComponents:
        stoneComponents.length === 2
          ? stoneComponents.map((component) => ({
              left: (((component.left + component.right) / 2 - left) / cropWidth) * 100,
              top: ((component.bottom - top + 1) / cropHeight) * 100,
              width: Math.min(82, ((component.right - component.left + 1) / cropWidth) * 115),
            }))
          : null,
    };
  };

  const appendContactShadows = (container, shadowComponents) => {
    (shadowComponents || [{}]).forEach((shadowBounds) => {
      const shadow = document.createElement('span');
      shadow.className = 'tf-contact-shadow';
      shadow.setAttribute('aria-hidden', 'true');
      if (shadowComponents) {
        shadow.style.setProperty('--tf-shadow-left', `${shadowBounds.left}%`);
        shadow.style.setProperty('--tf-shadow-top', `${shadowBounds.top}%`);
        shadow.style.setProperty('--tf-shadow-width', `${shadowBounds.width}%`);
      }
      container.appendChild(shadow);
    });
  };

  const mapContactShadowsToSource = (bounds) =>
    (bounds.shadowComponents || [{ left: 50, top: 100, width: 82 }]).map((shadowBounds) => ({
      left: ((bounds.left + (shadowBounds.left / 100) * bounds.width) / bounds.sourceWidth) * 100,
      top: ((bounds.top + (shadowBounds.top / 100) * bounds.height) / bounds.sourceHeight) * 100,
      width: (((shadowBounds.width / 100) * bounds.width) / bounds.sourceWidth) * 100,
    }));

  const prepareStoneStage = async (slide, titleStoneGroup, mediaIndex) => {
    const media = slide.querySelector('.product__media');
    const image = media?.querySelector('img');
    if (!media || !image) return null;

    const bounds = await findAlphaBounds(image);
    if (!bounds) return null;

    const ratio = bounds.width / bounds.height;
    const baseStageWidth = bounds.isMultiStone ? Math.min(80, 72 * ratio) : Math.min(62, 56 * ratio);
    const showContactShadow = titleStoneGroup !== 'multiple' && bounds.stoneCount < 3;
    const mediaScale = mediaIndex === 0 ? 0.8 : mediaIndex === 1 ? 0.82 : 0.66;
    const stageWidth = Math.round(baseStageWidth * mediaScale * 100) / 100;
    const stage = document.createElement('div');
    const crop = document.createElement('div');

    stage.className = 'tf-product-stone-stage';
    crop.className = 'tf-product-stone-crop';

    stage.style.setProperty('--tf-stone-ratio', ratio);
    stage.style.setProperty('--tf-stage-width', `${stageWidth}%`);
    stage.style.setProperty('--tf-dimension-stage-width', `${Math.min(78, 72 * ratio)}%`);
    stage.style.setProperty('--tf-source-width', `${(bounds.sourceWidth / bounds.width) * 100}%`);
    stage.style.setProperty('--tf-source-height', `${(bounds.sourceHeight / bounds.height) * 100}%`);
    stage.style.setProperty('--tf-source-left', `${(-bounds.left / bounds.width) * 100}%`);
    stage.style.setProperty('--tf-source-top', `${(-bounds.top / bounds.height) * 100}%`);

    crop.appendChild(image);
    stage.appendChild(crop);
    if (showContactShadow) {
      appendContactShadows(stage, bounds.shadowComponents);
    }
    media.appendChild(stage);

    return {
      slide,
      stage,
      ratio,
      dimensionRatio: bounds.dimensionWidth / bounds.dimensionHeight,
      dimensionLeft: bounds.dimensionLeft,
      dimensionTop: bounds.dimensionTop,
      dimensionWidth: bounds.dimensionWidth,
      dimensionHeight: bounds.dimensionHeight,
      sourceWidth: bounds.sourceWidth,
      sourceHeight: bounds.sourceHeight,
    };
  };

  const initializeCatalogCardMedia = (root = document) => {
    root.querySelectorAll('.tf-catalog-stone-placement > img').forEach((image) => {
      if (image.dataset.tfCardMediaReady === 'true') return;
      image.dataset.tfCardMediaReady = 'true';

      const placement = image.parentElement;
      if (!placement || placement.classList.contains('tf-catalog-stone-placement--multiple')) {
        return;
      }

      findAlphaBounds(image)
        .then((bounds) => {
          if (!bounds) return;

          if (bounds.stoneCount > 2) {
            placement.classList.add('tf-catalog-stone-placement--multiple');
            return;
          }

          appendContactShadows(placement, mapContactShadowsToSource(bounds));
        })
        .catch(() => null);
    });
  };

  const addDimensionsView = (gallery, prepared, onMediaSelected) => {
    const length = formatMillimeters(gallery.dataset.tfLength);
    const width = formatMillimeters(gallery.dataset.tfWidth);
    const viewer = gallery.querySelector('[id^="Slider-Gallery-"]');
    const thumbnailList = gallery.querySelector('[id^="Slider-Thumbnails-"]');

    if (!length || !width || !viewer || !thumbnailList) return;

    const sectionId = gallery.id.replace('MediaGallery-', '');
    const mediaId = `${sectionId}-tf-dimensions`;
    const horizontalLabel = prepared.ratio >= 1 ? length : width;
    const verticalLabel = prepared.ratio >= 1 ? width : length;
    const slide = document.createElement('li');
    const view = document.createElement('div');
    const stoneArea = document.createElement('div');
    const horizontalLine = document.createElement('div');
    const verticalLine = document.createElement('div');
    const createDimensionStage = (isThumbnail = false) => {
      const stage = prepared.stage.cloneNode(true);
      const image = stage.querySelector('img');
      stage.querySelectorAll('.tf-contact-shadow').forEach((shadow) => shadow.remove());
      if (!isThumbnail) {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
      }
      stage.style.setProperty('--tf-source-left', `${(-prepared.dimensionLeft / prepared.dimensionWidth) * 100}%`);
      stage.style.setProperty('--tf-source-top', `${(-prepared.dimensionTop / prepared.dimensionHeight) * 100}%`);
      stage.style.setProperty('--tf-source-width', `${(prepared.sourceWidth / prepared.dimensionWidth) * 100}%`);
      stage.style.setProperty('--tf-source-height', `${(prepared.sourceHeight / prepared.dimensionHeight) * 100}%`);
      stage.style.setProperty('--tf-stone-ratio', prepared.dimensionRatio);
      stage.style.setProperty('--tf-dimension-stage-width', `${Math.min(78, 72 * prepared.dimensionRatio)}%`);
      appendContactShadows(stage);
      return stage;
    };
    const activateMedia = (targetId) => {
      const activeSlide = viewer.querySelector(`[data-media-id="${targetId}"]`);
      if (!activeSlide) return;

      viewer.querySelectorAll('[data-media-id]').forEach((item) => {
        item.classList.toggle('is-active', item === activeSlide);
      });
      thumbnailList.querySelectorAll('.thumbnail-list__item[data-target]').forEach((item) => {
        const button = item.querySelector('button');
        if (!button) return;
        if (item.dataset.target === targetId) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    };

    slide.id = `Slide-${sectionId}-tf-dimensions`;
    slide.className = 'product__media-item grid__item slider__slide';
    slide.dataset.mediaId = mediaId;
    view.className = 'tf-product-dimensions-view';
    stoneArea.className = 'tf-product-dimension-stone';
    horizontalLine.className = 'tf-product-dimension-line tf-product-dimension-line--horizontal';
    verticalLine.className = 'tf-product-dimension-line tf-product-dimension-line--vertical';
    horizontalLine.innerHTML = `<span class="tf-product-dimension-label">${horizontalLabel}</span>`;
    verticalLine.innerHTML = `<span class="tf-product-dimension-label">${verticalLabel}</span>`;
    const dimensionStage = createDimensionStage();
    dimensionStage.appendChild(horizontalLine);
    dimensionStage.appendChild(verticalLine);
    stoneArea.appendChild(dimensionStage);
    view.appendChild(stoneArea);
    slide.appendChild(view);
    viewer.appendChild(slide);

    const thumbnailItem = document.createElement('li');
    const thumbnailButton = document.createElement('button');
    const thumbnailPreview = document.createElement('span');
    const thumbnailLabel = document.createElement('span');

    thumbnailItem.id = `Slide-Thumbnails-${sectionId}-tf-dimensions`;
    thumbnailItem.className = 'thumbnail-list__item slider__slide';
    thumbnailItem.dataset.target = mediaId;
    thumbnailItem.dataset.mediaPosition = String(thumbnailList.children.length + 1);
    thumbnailButton.className = 'thumbnail global-media-settings global-media-settings--no-shadow';
    thumbnailButton.type = 'button';
    thumbnailButton.setAttribute('aria-label', 'Load dimensions view');
    thumbnailButton.setAttribute('aria-controls', `GalleryViewer-${sectionId}`);
    thumbnailPreview.className = 'tf-product-dimension-thumbnail';
    thumbnailLabel.className = 'tf-product-dimension-thumbnail-label';
    thumbnailLabel.textContent = 'DIM';
    thumbnailPreview.appendChild(createDimensionStage(true));
    thumbnailButton.appendChild(thumbnailPreview);
    thumbnailButton.appendChild(thumbnailLabel);
    thumbnailItem.appendChild(thumbnailButton);
    thumbnailList.appendChild(thumbnailItem);

    requestAnimationFrame(() => {
      viewer.closest('slider-component')?.resetPages?.();
      thumbnailList.closest('slider-component')?.resetPages?.();
    });

    thumbnailList.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('.thumbnail');
        const item = button?.closest('.thumbnail-list__item[data-target]');
        if (!item) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        activateMedia(item.dataset.target);
        onMediaSelected?.(viewer.querySelector(`[data-media-id="${item.dataset.target}"]`));
      },
      true
    );

    const total = gallery.querySelector('.slider-counter--total');
    if (total) total.textContent = String(viewer.querySelectorAll('[data-media-id]').length);
  };

  const initializeGallery = async (gallery) => {
    if (gallery.dataset.tfProductMediaReady === 'true') return;
    gallery.dataset.tfProductMediaReady = 'true';
    gallery.classList.add('tf-product-media');

    const imageSlides = Array.from(
      gallery.querySelectorAll('.product__media-item:has(.product__modal-opener--image img)')
    );
    if (!imageSlides.length) return;

    const title = gallery.closest('product-info')?.querySelector('.product__title')?.textContent || '';
    let titleStoneGroup = 'single';
    if (/\bpair\b/i.test(title)) titleStoneGroup = 'pair';
    else if (/\b(set|suite|lot|parcel)\b/i.test(title)) titleStoneGroup = 'multiple';
    const preparedBySlide = new Map();
    const prepareSlide = (slide) => {
      if (!preparedBySlide.has(slide)) {
        preparedBySlide.set(
          slide,
          prepareStoneStage(slide, titleStoneGroup, imageSlides.indexOf(slide)).catch(() => null)
        );
      }
      return preparedBySlide.get(slide);
    };
    const activeSlide = imageSlides.find((slide) => slide.classList.contains('is-active')) || imageSlides[0];
    const activeImage = await prepareSlide(activeSlide);
    if (!activeImage) return;

    addDimensionsView(gallery, activeImage, (slide) => {
      if (slide && slide !== activeSlide) prepareSlide(slide);
    });

    const prepareRemaining = async () => {
      for (const slide of imageSlides) {
        if (slide !== activeSlide) await prepareSlide(slide);
      }
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(prepareRemaining, { timeout: 2500 });
    } else {
      window.setTimeout(prepareRemaining, 600);
    }
  };

  document.querySelectorAll('product-info media-gallery').forEach((gallery) => {
    initializeGallery(gallery);
  });
  initializeCatalogCardMedia();
  new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initializeCatalogCardMedia(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
